'use client';

import React, { useEffect, useState, Suspense } from 'react';
import { AuthService, AuthErrorType } from '@/lib/services/auth-service';
import { AuthFallbackHandler } from '@/lib/auth/fallback-handler';
import { AuthMonitoring } from '@/lib/auth/monitoring';
import { FeatureFlag, getFeatureFlag } from '@/lib/config/features';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { usePathname } from 'next/navigation';
import { isPublicRoute } from '@/lib/utils/route-utils';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackComponent?: React.ReactNode;
  maxRecoveryAttempts?: number;
  withSuspense?: boolean; // Wrap with suspense boundary for async components
  publicRouteMode?: boolean; // Less strict error handling for public routes
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  recoveryAttempted: boolean;
  recoveryCount: number;
  recoveryInProgress: boolean;
  lastRecoveryTime: number | null;
  errorType: AuthErrorType;
}

/**
 * Enhanced Auth error boundary component that attempts to recover from auth errors
 * This provides a fallback UI when auth errors occur and advanced recovery options
 */
export class AuthErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Default maximum recovery attempts
  static defaultProps = {
    maxRecoveryAttempts: 2,
    withSuspense: true,
    publicRouteMode: false
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      recoveryAttempted: false,
      recoveryCount: 0,
      recoveryInProgress: false,
      lastRecoveryTime: null,
      errorType: AuthErrorType.UNKNOWN_ERROR
    };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Determine error type based on error message patterns
    let errorType = AuthErrorType.UNKNOWN_ERROR;
    
    const errorMessage = error.message?.toLowerCase() || '';
    if (errorMessage.includes('auth') || errorMessage.includes('session') || errorMessage.includes('token')) {
      errorType = AuthErrorType.AUTHENTICATION_ERROR;
    } else if (errorMessage.includes('profile')) {
      errorType = AuthErrorType.PROFILE_ERROR;
    } else if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('connection')) {
      errorType = AuthErrorType.NETWORK_ERROR;
    } else if (errorMessage.includes('server') || errorMessage.includes('500')) {
      errorType = AuthErrorType.SERVER_ERROR;
    }
    
    this.setState({ 
      errorInfo,
      errorType
    });
    
    // Track the error in our monitoring system
    AuthMonitoring.trackError({
      type: errorType,
      message: error.message || 'Unknown error in React component',
      originalError: error
    }, false);
    
    console.error(`[AuthErrorBoundary] Caught an error of type ${errorType}:`, error, errorInfo);
  }

  async attemptRecovery() {
    const { maxRecoveryAttempts } = this.props;
    
    // Avoid recovery if we've already tried too many times
    if (this.state.recoveryCount >= (maxRecoveryAttempts || 2)) {
      console.warn('[AuthErrorBoundary] Maximum recovery attempts reached');
      return;
    }
    
    // Avoid parallel recovery attempts
    if (this.state.recoveryInProgress) return;
    
    // Avoid too frequent recovery attempts (minimum 3 second gap)
    const now = Date.now();
    if (this.state.lastRecoveryTime && now - this.state.lastRecoveryTime < 3000) {
      console.warn('[AuthErrorBoundary] Recovery attempted too frequently, throttling');
      return;
    }
    
    this.setState({ 
      recoveryAttempted: true,
      recoveryInProgress: true,
      recoveryCount: this.state.recoveryCount + 1,
      lastRecoveryTime: now
    });
    
    const startTime = Date.now();
    
    try {
      console.log(`[AuthErrorBoundary] Attempting recovery (attempt ${this.state.recoveryCount + 1})`);
      
      // Try multiple recovery strategies in sequence
      
      // 1. First try to refresh the session (simplest recovery)
      try {
        console.log('[AuthErrorBoundary] Trying session refresh...');
        await AuthService.refreshSession();
        
        // If we got here, recovery succeeded
        console.log('[AuthErrorBoundary] Session refresh successful, recovering');
        
        // Track recovery
        AuthMonitoring.trackRecovery('refresh', this.state.errorType, {
          durationMs: Date.now() - startTime,
          specializedMethod: 'refreshSession'
        });
        
        this.setState({ 
          hasError: false, 
          error: null, 
          errorInfo: null,
          recoveryInProgress: false
        });
        
        return;
      } catch (refreshError) {
        console.warn('[AuthErrorBoundary] Session refresh failed, trying other methods');
      }
      
      // 2. Try fallback handler as the next option
      if (getFeatureFlag(FeatureFlag.USE_FALLBACK_HANDLER) && this.state.error) {
        try {
          console.log('[AuthErrorBoundary] Trying legacy fallback...');
          const recovered = await AuthFallbackHandler.tryRecover({
            type: this.state.errorType,
            message: this.state.error.message || 'Unknown error in component',
            originalError: this.state.error
          });
          
          if (recovered) {
            console.log('[AuthErrorBoundary] Legacy fallback successful');
            
            // Track recovery in our monitoring system
            AuthMonitoring.trackRecovery('fallback', this.state.errorType, {
              durationMs: Date.now() - startTime
            });
            
            this.setState({ 
              hasError: false, 
              error: null, 
              errorInfo: null,
              recoveryInProgress: false
            });
            
            return;
          }
        } catch (fallbackError) {
          console.error('[AuthErrorBoundary] Legacy fallback failed:', fallbackError);
        }
      }
      
      // 3. Last resort: Force a deep refresh by clearing auth cache and reloading
      if (this.state.recoveryCount >= (maxRecoveryAttempts || 2) - 1) {
        console.log('[AuthErrorBoundary] Attempting last resort deep refresh...');
        
        // Clear any auth-related cache
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('auth_profile_cache');
          localStorage.removeItem('auth_session_cache');
        }
        
        // Wait a moment, then reload the page for a fresh start
        setTimeout(() => {
          window.location.reload();
        }, 500);
        
        this.setState({ recoveryInProgress: false });
        return;
      }
      
      // If we got here, all recovery attempts failed
      console.error('[AuthErrorBoundary] All recovery attempts failed');
      this.setState({ recoveryInProgress: false });
      
    } catch (error) {
      console.error('[AuthErrorBoundary] Unexpected error during recovery:', error);
      this.setState({ recoveryInProgress: false });
    }
  }

  componentDidMount() {
    // Register the fallback handler listeners when the component mounts
    AuthFallbackHandler.registerFallbackListeners();
  }

  render() {
    // Allow auth errors to pass through on public routes if in public route mode
    if (this.props.publicRouteMode && 
        (this.state.error?.message?.includes('auth') || 
         this.state.error?.message?.includes('Auth'))) {
      console.log('[AuthErrorBoundary] Ignoring auth error on public route:', this.state.error?.message);
      return this.props.children;
    }
    
    // Handle specific auth context errors first
    if (this.state.error?.message?.includes('useAuthContext') || 
        this.state.error?.message?.includes('useSupabaseAuth')) {
      console.error('[AuthErrorBoundary] Auth provider initialization error detected');
      
      // Special handling for auth context initialization errors
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="max-w-md w-full p-8 bg-card border rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4 text-foreground">Authentication Initialization Error</h2>
            <p className="text-muted-foreground mb-6">
              We encountered an issue while initializing authentication. This usually happens when components try to access 
              authentication before it's fully initialized.
            </p>
            
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      // Auto-attempt recovery once
      if (!this.state.recoveryAttempted) {
        this.attemptRecovery();
      }
      
      // Use custom fallback component if provided
      if (this.props.fallbackComponent) {
        return this.props.fallbackComponent;
      }
      
      // Default error UI
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="max-w-md w-full p-8 bg-card border rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4 text-foreground">Authentication Error</h2>
            <p className="text-muted-foreground mb-6">
              We encountered an issue with your authentication. Please try refreshing the page or logging in again.
            </p>
            
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Refresh Page
              </button>
              
              <button
                onClick={() => window.location.href = '/login'}
                className="py-2 px-4 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90"
              >
                Go to Login
              </button>
              
              <button
                onClick={() => this.attemptRecovery()}
                disabled={this.state.recoveryInProgress}
                className="py-2 px-4 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {this.state.recoveryInProgress ? 'Recovering...' : 'Try Again'}
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-6 p-3 bg-red-50 dark:bg-red-900/30 rounded text-xs overflow-auto max-h-32">
                <div className="font-semibold text-red-700 dark:text-red-300">Error Type: {this.state.errorType}</div>
                <div className="text-red-600 dark:text-red-400">{this.state.error.message}</div>
                <div className="mt-2 text-red-500 dark:text-red-400">
                  Recovery attempts: {this.state.recoveryCount}/{this.props.maxRecoveryAttempts}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Wrap children in suspense if needed
    if (this.props.withSuspense) {
      return (
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-screen bg-background">
            <div className="h-8 w-8 rounded-full border-4 border-primary border-r-transparent animate-spin"></div>
          </div>
        }>
          {this.props.children}
        </Suspense>
      );
    }
    
    return this.props.children;
  }
}